import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@react-navigation/native';
import { ImagePickerAsset } from 'expo-image-picker';
import { DeepNonNullable } from 'utility-types';

import { showErrorToUser, preparePhotoAsset } from '@pletly/ui';
import { prepareMediaFile, processSelectedMedia } from '@pletly/common';
import { cleanInfoItems } from '@pletly/shared';

import { useAuth, useLoadingOverlay } from '%context';
import { GetProtegesDocument } from '%context/Connections/__generated__/getProteges.generated';

import { useUser } from '%hooks';

import { GetUserInfoDocument } from '%components/Profile/__generated__/userInfo.generated';
import { GetCurrentUserPostsDocument } from '%context/Posts/__generated__/userPosts.generated';
import {
	UpdateUserMutationVariables,
	useUpdateUserMutation,
} from '%screens/editProfile/__generated__/updateUser.generated';
import { useDeleteAccount } from './useDeleteAccount';

export const useEditProfile = <User extends { id: string; username?: string }>(profile: User) => {
	const { t } = useTranslation();
	const { goBack, navigate } = useNavigation();

	const [updateUser] = useUpdateUserMutation();

	const { showLoadingOverlay, closeLoadingOverlay } = useLoadingOverlay();
	const { renewToken } = useAuth();
	const { id: loggedUserId } = useUser();

	const isCurrentUser = loggedUserId === profile.id;

	const successSaveNavigate = useMemo(
		() => (isCurrentUser ? goBack : () => navigate('Connections')),
		[isCurrentUser, goBack, navigate],
	);

	const handleImageChange = useCallback(
		async (result: ImagePickerAsset) => {
			showLoadingOverlay(t('profile.saving-profile-photo'));
			try {
				const media = await processSelectedMedia(result);
				const file = await prepareMediaFile(media);

				await updateUser({
					variables: {
						id: String(profile.id),
						media: file,
					},
					refetchQueries: [isCurrentUser ? GetCurrentUserPostsDocument : GetProtegesDocument],
				});
				if (isCurrentUser) {
					await renewToken();
				}
				closeLoadingOverlay();
				successSaveNavigate();
			} catch (error) {
				console.error(error);
				closeLoadingOverlay();
			}
		},
		[
			isCurrentUser,
			profile.id,
			showLoadingOverlay,
			closeLoadingOverlay,
			renewToken,
			updateUser,
			successSaveNavigate,
			t,
		],
	);

	const updateUserInfo = useCallback(
		async (
			{
				email,
				username,
				firstName,
				lastName,
				info,
				criticalInfo: criticalItems,
				negativeInfo: negativeItems,
				positiveInfo: positiveItems,
				restrictedAccess,
				receiveSummaryNotificationEmails,
				media,
				tags,
			}: Partial<DeepNonNullable<UpdateUserMutationVariables>>,
			{ onError, onSuccess }: { onError?: () => unknown; onSuccess?: () => unknown } = {},
		) => {
			try {
				showLoadingOverlay(t('profile.saving-user-information'));
				const photoMediaFile = await preparePhotoAsset(media);

				await updateUser({
					variables: {
						id: String(profile.id),
						email: email,
						username: username,
						firstName: firstName,
						lastName: lastName,
						info: info?.trim(),
						criticalInfo: cleanInfoItems(criticalItems),
						negativeInfo: cleanInfoItems(negativeItems),
						positiveInfo: cleanInfoItems(positiveItems),
						restrictedAccess,
						receiveSummaryNotificationEmails,
						media: photoMediaFile,
						tags,
					},
					refetchQueries: isCurrentUser
						? [GetCurrentUserPostsDocument, GetUserInfoDocument]
						: [GetProtegesDocument],
				});

				if (isCurrentUser) {
					await renewToken();
				}
				onSuccess?.();
			} catch (error) {
				console.error(error);
				onError?.();
				if (error instanceof Error) {
					showErrorToUser(error);
				}
			} finally {
				closeLoadingOverlay();
			}
		},
		[t, isCurrentUser, profile.id, showLoadingOverlay, closeLoadingOverlay, renewToken, updateUser],
	);

	const { handleDeleteAccount } = useDeleteAccount(profile);

	return { handleDeleteAccount, handleImageChange, updateUserInfo };
};
